import React from 'react';
import styled, { css } from 'styled-components';
import { colors } from '../../styles/colors';
import { typography } from '../../styles/typography';
import { spacing } from '../../styles/spacing';

// Button variants
const buttonVariants = {
  primary: css`
    background: ${colors.gradients.primary};
    color: ${colors.text.white};
    border: none;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${colors.shadows.lg};
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `,
  secondary: css`
    background: ${colors.background.primary};
    color: ${colors.text.primary};
    border: 1px solid ${colors.border.interactive};
    
    &:hover {
      background: ${colors.background.secondary};
      border-color: ${colors.border.interactive};
      transform: translateY(-1px);
      box-shadow: ${colors.shadows.md};
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `,
  ghost: css`
    background: transparent;
    color: ${colors.text.primary};
    border: none;
    
    &:hover {
      background: ${colors.background.secondary};
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `,
  outline: css`
    background: transparent;
    color: ${colors.primary.DEFAULT};
    border: 1px solid ${colors.primary.DEFAULT};
    
    &:hover {
      background: ${colors.primary.light}20;
      transform: translateY(-1px);
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  `,
};

// Button sizes
const buttonSizes = {
  sm: css`
    padding: ${spacing[2]} ${spacing[3]};
    font-size: ${typography.sizes.sm};
    border-radius: ${spacing.radius.lg};
  `,
  md: css`
    padding: ${spacing[3]} ${spacing[6]};
    font-size: ${typography.sizes.base};
    border-radius: ${spacing.radius.xl};
  `,
  lg: css`
    padding: ${spacing[4]} ${spacing[8]};
    font-size: ${typography.sizes.lg};
    border-radius: ${spacing.radius['2xl']};
  `,
  xl: css`
    padding: ${spacing[5]} ${spacing[10]};
    font-size: ${typography.sizes.xl};
    border-radius: ${spacing.radius['3xl']};
  `,
};

// Styled Button component
const StyledButton = styled.button<{
  $variant: keyof typeof buttonVariants;
  $size: keyof typeof buttonSizes;
  $fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  font-family: ${typography.fonts.primary};
  font-weight: ${typography.weights.medium};
  line-height: ${typography.lineHeights.none};
  letter-spacing: ${typography.letterSpacing.wide};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  overflow: hidden;
  
  ${props => buttonVariants[props.$variant]}
  ${props => buttonSizes[props.$size]}
  
  ${props => props.$fullWidth && css`
    width: 100%;
  `}
  
  /* Loading state */
  &[data-loading="true"] {
    pointer-events: none;
    
    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Icon wrapper for consistent spacing
const IconWrapper = styled.span<{ $size: keyof typeof buttonSizes }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch (props.$size) {
      case 'sm': return css`width: 16px; height: 16px;`;
      case 'md': return css`width: 20px; height: 20px;`;
      case 'lg': return css`width: 24px; height: 24px;`;
      case 'xl': return css`width: 28px; height: 28px;`;
      default: return css`width: 20px; height: 20px;`;
    }
  }}
`;

// Button props interface
interface ButtonProps {
  variant?: keyof typeof buttonVariants;
  size?: keyof typeof buttonSizes;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

// Main Button component
const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  children,
  onClick,
  disabled,
  type = 'button',
  className,
}) => {
  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      data-loading={loading}
      onClick={onClick}
      type={type}
      className={className}
    >
      {loading ? (
        <>
          <span style={{ opacity: 0 }}>{children}</span>
        </>
      ) : (
        <>
          {leftIcon && <IconWrapper $size={size}>{leftIcon}</IconWrapper>}
          {children}
          {rightIcon && <IconWrapper $size={size}>{rightIcon}</IconWrapper>}
        </>
      )}
    </StyledButton>
  );
};

export default Button;